import { PureComponent, Fragment } from 'react';
import {
  CardNumberElement,
  CardExpiryElement,
  CardCVCElement,
  PostalCodeElement,
  injectStripe,
} from 'react-stripe-elements';
import { withRouter, Redirect } from 'react-router-dom';
import {
  toPairs,
  isNull,
  set,
  assign,
  isUndefined,
  isArray,
  compact,
  values as _values,
  isEmpty,
  find,
} from 'lodash-es';
import { getMeta } from 'constants/meta';
import Icon from 'components/Icon';
import Select from 'components/Select';
import Checkbox from 'components/Checkbox';
import Button from 'components/Button';
import Head from 'components/Head';
import HasError from 'hocs/HasError';
import HasLabel from 'hocs/HasLabel';
import { InputHasLabelError } from 'components/Input/InputVariants';
import { apiCheckout, getPlanDetails } from 'constants/api';
import { to } from 'helpers';
import { getCountries } from 'helpers/json';
import { validateValues } from 'helpers/validation';

import StripeInputAdapter from './StripeInputAdapter';

import {
  CHECKOUT_FIELDS_VALUES,
  STRIPE_REQUIRED_ERRORS,
  STRIPE_VALIDATION_ERRORS,
} from './constants';

const CardNumber = StripeInputAdapter(CardNumberElement);
const CardExpiry = StripeInputAdapter(CardExpiryElement);
const CardCVC = StripeInputAdapter(CardCVCElement);
const CardPostalCode = StripeInputAdapter(PostalCodeElement);

let SelectHasLabelError;

const TermsText = (
  <Fragment>
    I agree to standard{' '}
    <a
      href="/legal/terms"
      className="link link_blue link__checkbox"
      target="_blank"
    >
      Terms&nbsp;of&nbsp;Service
    </a>{' '}
    and{' '}
    <a
      href="/legal/privacy"
      className="link link_blue link__checkbox"
      target="_blank"
    >
      {' '}
      Privacy&nbsp;Policy{' '}
    </a>{' '}
    and understand my subscription will start today
  </Fragment>
);

const cardBrandToIcon = {
  visa: 'visa-card',
  mastercard: 'master-card',
  amex: 'amex-card',
  discover: 'discover-card',
  diners: 'diners-card',
  jcb: 'jcb-card',
  unionpay: 'unionpay-card',
  unknown: 'default-card',
  undefined: 'error-card',
};

class CheckoutForm extends PureComponent {
  constructor(props) {
    super(props);

    this.user = props.location.state;
    SelectHasLabelError = HasLabel(HasError(Select));
  }

  state = {
    isVisible: false,
    isChecked: false,
    submitting: false,
    brand: 'unknown',
    errors: {},
    values: {
      ...CHECKOUT_FIELDS_VALUES,
    },
  };

  values = {
    cardCvc: true,
    postalCode: true,
    cardExpiry: true,
    cardNumber: true,
  };

  nonStripeErrors = {};

  stripeErrors = {};

  async componentDidMount() {
    if (this.user) {
      const { type, uid } = this.user;
      const { data } = await apiCheckout.getAccountDetails(type, uid);
      this.accountDetails = data;

      const request = await getPlanDetails();
      const plan = request.data[type];
      const { marketingName, periods } = plan;

      const sum = find(
        periods,
        (item) => item.periodId === this.accountDetails.periodId
      );

      this.setState({
        plan: {
          name: marketingName,
          sum: sum.price,
        },
      });
    }
  }

  setNestedState = (parent, child, callback) =>
    this.setState(
      (prevState) => ({
        ...prevState,
        [parent]: { ...prevState[parent], ...child },
      }),
      callback
    );

  handleShowForm = () => {
    this.setState({ isVisible: true });
  };

  handleTermsCheckbox = () => {
    this.setState(({ isChecked }) => ({ isChecked: !isChecked }));
  };

  handleTokenGeneration = async () => {
    const { values } = this.state;
    const { token, error } = await this.props.stripe.createToken({
      address_city: values.city,
      address_country: values.country,
      address_line1: values.address,
      address_state: values.state,
    });
    const errorsToShow = {};
    const entries = toPairs(this.values);

    if (error && error.type === 'card_error') {
      errorsToShow.cardNumber = error.message;
    }

    entries.forEach(([key, value]) => {
      if (value) {
        errorsToShow[key] = STRIPE_REQUIRED_ERRORS[key];
      }

      if (this.stripeErrors[key] && !value) {
        errorsToShow[key] = STRIPE_VALIDATION_ERRORS[key];
      }
    });

    if (token) {
      return { token };
    }

    return { errorsToShow };
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    this.setState({ submitting: true });
    const { token, errorsToShow } = await this.handleTokenGeneration();
    const errors = validateValues(this.state.values);

    const errorsObject = assign({}, errors, errorsToShow);

    if (!isEmpty(compact(_values(errorsObject)))) {
      this.setState({ errors: errorsObject, submitting: false });
      return;
    }

    const { type, uid } = this.user;

    const [err, response] = await to(
      apiCheckout.createPayment(type, uid, {
        source: token.id,
      })
    );

    if (!isNull(err)) {
      if (isUndefined(err.response)) {
        this.setNestedState('errors', {
          submitErrors:
            'Network connection is temporary unavailable. Please try again.',
        });
        return this.setState({ submitting: false });
      }

      if (!err.response.data) {
        this.setState({ submitting: false });
        return;
      }

      const { message } = err.response.data;

      if (!isArray(message)) {
        this.setNestedState('errors', {
          submitErrors: message,
        });
      } else {
        message.forEach(({ field, message }) => {
          this.setNestedState('errors', {
            [field]: message,
          });
        });
      }

      this.setState({ submitting: false });
      return;
    }

    const { redirectUrl } = response.data;

    return window.location.replace(redirectUrl);
  };

  handleChange = ({ key, value }) => {
    this.setNestedState('values', { [key]: value });
    this.setNestedState('errors', { [key]: '', submitErrors: '' });
  };

  handleStripeChange = ({ elementType, empty, error, brand }) => {
    if (elementType === 'cardNumber' && this.state.brand !== brand) {
      this.setState({ brand });
    }
    set(this.values, `${elementType}`, empty);
    set(this.stripeErrors, `${elementType}`, false);
    this.setNestedState('errors', {
      [elementType]: '',
      submitErrors: '',
    });

    if (error) {
      set(this.stripeErrors, `${elementType}`, true);
    }
  };

  render() {
    const { location } = this.props;

    if (location.pathname === '/login') return null;

    const { user = { type: 'partner' } } = this;
    const { errors, values, submitting } = this.state;
    const { title, description } = getMeta(location.pathname)[user.type];

    const fontStyle = {
      base: {
        fontFamily:
          '"Gotham SSm A", "Gotham SSm B", -apple-system, system-ui, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
      },
      invalid: {
        color: '#0f141a',
      },
    };

    return (
      <Fragment>
        <Head title={title} description={description} />
        <div className="section section_checkout">
          <form
            className="wrapper"
            autoComplete="off"
            onSubmit={this.handleSubmit}
          >
            <h4 className="title section__main-title _mb-38">Pay & Activate</h4>
            <div className="row">
              <div className="col col-5 offset-1 offset-tablet-0 col-tablet-12">
                <div className="section__box _mt-19">
                  <h5 className="title section__title _mb-17">
                    Payment Method
                  </h5>

                  <div className="_mb-31">
                    <div className="form__row _mb-17">
                      <CardNumber
                        label="Card Number"
                        onReady={this.handleShowForm}
                        onChange={this.handleStripeChange}
                        error={errors.cardNumber || errors.submitErrors}
                        style={fontStyle}
                      />
                      <Icon
                        className="input__card-icon"
                        icon={cardBrandToIcon[this.state.brand]}
                      />
                    </div>
                    <div className="form__row form__row_flex">
                      <div className="form__col form__col_flex_none _w-134 _mb-17">
                        <CardExpiry
                          label="Card Expiry"
                          invalid={errors.cardExpiry}
                          onChange={this.handleStripeChange}
                          style={fontStyle}
                          placeholder="MM / YY"
                        />
                      </div>
                      <div className="form__col form__col_flex_none _w-120">
                        <CardCVC
                          label="Security Code"
                          invalid={errors.cardCvc}
                          onChange={this.handleStripeChange}
                          placeholder="123"
                          style={fontStyle}
                        />
                      </div>
                      <div className="input-error-text form__error">
                        {errors.cardExpiry || errors.cardCvc}
                      </div>
                    </div>
                  </div>

                  <h5 className="title section__title _mb-14">
                    Billing Details
                  </h5>
                  <div className="form__row _mb-17">
                    <InputHasLabelError
                      name="address"
                      placeholder="Street address, building, etc."
                      label="Address"
                      labelClassName="_mb-7"
                      onChange={this.handleChange}
                      error={errors.address}
                    />
                  </div>

                  <div className="form__row _mb-17">
                    <SelectHasLabelError
                      name="country"
                      value={values.country}
                      onChange={this.handleChange}
                      options={getCountries()}
                      searchable
                      placeholder="Select a Country"
                      error={errors.country}
                      className={cx({
                        error: errors.country,
                      })}
                      labelStaticClass="Select__label"
                      label="Country"
                      labelClassName="_mb-7"
                    />
                  </div>
                  <div className="form__row _mb-17">
                    <InputHasLabelError
                      label="City"
                      name="city"
                      placeholder="City"
                      labelClassName="_mb-7"
                      onChange={this.handleChange}
                      error={errors.city}
                    />
                  </div>
                  <div className="form__row form__row_flex ">
                    <div className="form__col _mb-note-17">
                      <InputHasLabelError
                        label="State"
                        name="state"
                        placeholder="State"
                        onChange={this.handleChange}
                        error={errors.state}
                        labelClassName="_mb-7"
                      />
                    </div>
                    <div className="form__col form__col_last">
                      <CardPostalCode
                        label="Postcode/ZIP"
                        placeholder="12345"
                        onChange={this.handleStripeChange}
                        error={errors.postalCode}
                        style={fontStyle}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="col col-5 col-tablet-12">
                <div className="order section__order _mt-tablet-40">
                  <h5 className="order__title _mb-22">Your Order</h5>
                  {this.state.plan && (
                    <Fragment>
                      <div className="order__info-row">
                        <span className="order__info-title">
                          {this.state.plan.name}
                        </span>
                        <span className="order__info-price">
                          ${this.state.plan.sum}
                          .00
                        </span>
                      </div>
                      <div className="order__info-description _mb-17">
                        1 year subscription
                      </div>
                      <div className="order__total _mb-21">
                        <span>Total</span>
                        <span>
                          ${this.state.plan.sum}
                          .00
                        </span>
                      </div>
                    </Fragment>
                  )}
                  <Checkbox
                    checked={this.state.isChecked}
                    onChange={this.handleTermsCheckbox}
                    name="terms"
                    text={TermsText}
                    className="checkbox__wrap_order _mb-16"
                  />
                  <Button
                    className={cx('btn_wide order__btn', {
                      btn_disabled:
                        !this.state.isChecked ||
                        !isEmpty(compact(_values(this.state.errors))),
                    })}
                    submitting={submitting}
                    disabled={
                      !this.state.isChecked ||
                      !isEmpty(compact(_values(this.state.errors)))
                    }
                  >
                    Purchase
                  </Button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </Fragment>
    );
  }
}

export default withRouter(injectStripe(CheckoutForm));
